import {fullWidth} from '@models/helpers.ts';
import React, {useMemo, useState} from "react";
import {createSearchParams, Link, useLocation} from "react-router-dom";
import {Alert, Box, Chip, ChipProps, Fade, SxProps, Tooltip} from "@mui/material";
import {PermissaoAplicacao} from "@models/seguranca/permissaoApp.ts";
import {useAuth} from "@contexts/hooks/UseAuth.tsx";
import lodash from "lodash";
import {useSearchParams} from "react-router";

export interface NavButtonProps<TValue = any, TSearchData = object> {
	onContextMenu?: (e: React.MouseEvent<HTMLElement>) => void,
	permissoesNecessarias?: PermissaoAplicacao[],
	tableMode?: boolean,
	hint?: string,
	onClick?: (t?: TValue) => void,
	displayMode?: keyof TValue,
	label?: (v: TValue) => string
	pathname?: string,
	searchData?: TSearchData,
	hash?: string,
	value: TValue,
	ignoreNavigate?: boolean,
	slotProps?: {
		chip?: Partial<ChipProps>,
	}
}

export function NavButton<TValue = any, TSearchData = object>(props: NavButtonProps<TValue, TSearchData>) {
	const location = useLocation();
	const [currentSearchParams] = useSearchParams()
	const {permissoes, hasAccess} = useAuth()
	const {searchData, pathname, slotProps, onContextMenu} = props;

	const tableModeProps = props.tableMode ?
		{...fullWidth} : {};

	const [alertOpen, setAlertOpen] = useState(false);

	const noContextHandler = () => {
		setAlertOpen(true)
		setTimeout(() => {
				setAlertOpen(false)
			}, 2000
		)
	}
	const searchParams = useMemo(() => {
		return createSearchParams({...searchData as object})
	}, [searchData])
	const isSamePath = useMemo(() => {
		return (location.pathname == pathname)
	}, [location.pathname, pathname, location.search, location.pathname]);

	const pathNameMatches = useMemo(() => {
		return lodash.isEqual(location.pathname?.trim().toLowerCase(), props.pathname?.trim().toLowerCase())
	}, [location.pathname, props.pathname]);

	const searchDataMatches = useMemo(() => {
		const currentKeys = Object.fromEntries(currentSearchParams)
		const sdKeys = Object.keys(searchData ?? {})
		return sdKeys.map(x => {
			const cv = (currentKeys as any)[x]
			const sdv = (searchData as any)[x]

			return cv?.toString() == sdv?.toString()
		}).every(x => x)

	}, [searchParams, currentSearchParams])

	const hashMatches = useMemo(() => {
		console.log(props.hash, location.hash)
		return props.hash == null ? true : props.hash == location.hash.substring(1);
	}, [props.hash, location.hash]);

	const alreadyInPage = useMemo(() => {
		return pathNameMatches && searchDataMatches && hashMatches;
	}, [pathNameMatches, searchDataMatches, hashMatches]);

	const currentLabel = useMemo<string>(() => {
		const dv = (props.value != null && props.displayMode != null) ? props.value[props.displayMode] as string : ''

		if (props.label == null) {
			return dv
		}

		const label = props.label(props.value)
		if (label == '' || label == null) return (props.displayMode != null ? props.value[props.displayMode] : '') as string

		return label

	}, [props.label, props.value, props.displayMode])
	const temPermissao = useMemo(() => {
		return hasAccess(props.permissoesNecessarias || 'AbrirAplicacao')
	}, [props.permissoesNecessarias, permissoes]);

	const chipProps = useMemo<React.ComponentProps<typeof Chip>>(() => {
		return {
			label: currentLabel,
			disabled: alreadyInPage || !temPermissao,
			color: slotProps?.chip?.color ?? (temPermissao ? slotProps?.chip?.color ?? 'default' : 'error'),
			sx: {...slotProps?.chip?.sx, ...tableModeProps} as SxProps | undefined,
		} as React.ComponentProps<typeof Chip>
	}, [location, alreadyInPage, temPermissao, slotProps])

	const disabled = alreadyInPage || !temPermissao;

	const linkProps = useMemo<React.ComponentProps<typeof Link>>(() => {
		return {
			to: {
				pathname: pathname,
				search: searchParams.toString(),
				hash: props.hash ?? (isSamePath && location.hash || ''),
			},
			style: {
				cursor: !temPermissao ? 'not-allowed' : !disabled ? 'pointer' : 'default',
			},
			onContextMenu: (e: any) => {
				if (onContextMenu != null) {
					onContextMenu(e as any)
					return
				}
				noContextHandler()
			},
			onClick: (e: React.MouseEvent) => {
				if (props.ignoreNavigate || disabled) {
					e.preventDefault()
				}
				if (!disabled) {
					if (props.onClick != null) {
						props.onClick(props.value)
					}
				}
			}
		} as React.ComponentProps<typeof Link>
	}, [props, isSamePath, searchDataMatches, pathname, temPermissao, location.search])


	return (<Box>
		<Tooltip title={temPermissao ? props.hint : 'Sem permissão para acessar o recurso'}
		         placement={'top'}
		         enterDelay={500}
		         enterNextDelay={250}>
			<Link reloadDocument={false}
			      {...linkProps}>
				<Chip size={'small'}
				      {...chipProps}
				/>
			</Link>
		</Tooltip>
		<Fade style={{zIndex: 2, position: 'fixed'}} in={alertOpen} timeout={{enter: 250, exit: 250}}>
			<Alert variant={'standard'} color={'info'}
			       icon={false}
			       sx={{
				       position: 'absolute',
				       opacity: 0.85,
				       zIndex: 300000
			       }}>{'Não existem ações rápidas para esse item'}</Alert>
		</Fade>
	</Box>)
}